import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Receipt, Plus, Edit, Trash2, X, Check, Building } from 'lucide-react';
import {
  getCrecheTariffs,
  createCrecheTariff,
  updateCrecheTariff,
  deleteCrecheTariff,
  getStructures,
} from '../../api';
import { useToast } from '../../contexts/NotificationContext';

const EMPTY_FORM = {
  structureIds: [],
  name: '',
  days: 5,
  monthlyAmount: '',
  isPublicEmployer: false,
  validFrom: '',
  validUntil: '',
  notes: '',
};

const fmtCurrency = (v) =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(v);

const fmtDate = (d) => {
  if (!d) return null;
  return new Date(d).toLocaleDateString('fr-FR');
};

const PEBadge = ({ isPE }) =>
  isPE ? (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
      PE
    </span>
  ) : null;

const StructureBadge = ({ name, color }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${color}`}>
    {name}
  </span>
);

// ─── Modal ───────────────────────────────────────────────────────────────────

const TariffModal = ({ open, title, formData, onChange, onSubmit, onClose, loading, structures, isEditing }) => {
  if (!open) return null;

  const toggleStructure = (id) => {
    const current = formData.structureIds || [];
    const next = current.includes(id)
      ? current.filter((s) => s !== id)
      : [...current, id];
    onChange('structureIds', next);
  };

  const selectAll = () => onChange('structureIds', structures.map((s) => s.id));
  const allSelected = structures.length > 0 && (formData.structureIds || []).length === structures.length;
  const selectedCount = (formData.structureIds || []).length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 overflow-y-auto max-h-[90vh]">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-semibold text-gray-800">{title}</h2>
          <button type="button" onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition">
            <X size={20} />
          </button>
        </div>
        <form onSubmit={onSubmit} className="px-6 py-5 space-y-4">
          {/* Structures — checkboxes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Structure(s) <span className="text-rose-500">*</span>
            </label>
            <div className="space-y-2">
              {structures.length > 1 && (
                <button
                  type="button"
                  onClick={() => allSelected ? onChange('structureIds', []) : selectAll()}
                  className={`text-xs px-2 py-1 rounded-md transition ${
                    allSelected ? 'bg-rose-100 text-rose-700 hover:bg-rose-200' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {allSelected ? 'Tout désélectionner' : 'Toutes les structures'}
                </button>
              )}
              <div className="flex flex-wrap gap-3">
                {structures.map((s) => {
                  const checked = (formData.structureIds || []).includes(s.id);
                  return (
                    <label
                      key={s.id}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition ${
                        checked ? 'border-rose-400 bg-rose-50 text-rose-700' : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="checkbox"
                        className="w-4 h-4 rounded border-gray-300 text-rose-500 focus:ring-rose-400"
                        checked={checked}
                        onChange={() => toggleStructure(s.id)}
                      />
                      <span className="text-sm font-medium">{s.name}</span>
                    </label>
                  );
                })}
              </div>
              {selectedCount === 0 && (
                <p className="text-xs text-rose-500">Sélectionnez au moins une structure</p>
              )}
            </div>
          </div>

          {/* Nom */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Intitulé du tarif <span className="text-rose-500">*</span>
            </label>
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-rose-400 text-sm"
              value={formData.name}
              onChange={(e) => onChange('name', e.target.value)}
              placeholder="ex : Forfait 5 jours"
              required
              autoFocus
            />
          </div>

          {/* Jours + Montant */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Jours / semaine <span className="text-rose-500">*</span>
              </label>
              <input
                type="number" min="1" max="5"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-rose-400 text-sm"
                value={formData.days}
                onChange={(e) => onChange('days', e.target.value)}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Montant mensuel (€) <span className="text-rose-500">*</span>
              </label>
              <input
                type="number" min="0" step="0.01"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-rose-400 text-sm"
                value={formData.monthlyAmount}
                onChange={(e) => onChange('monthlyAmount', e.target.value)}
                placeholder="0.00"
                required
              />
            </div>
          </div>

          {/* PE */}
          <div className="flex items-center gap-3">
            <input
              type="checkbox" id="isPublicEmployer"
              className="w-4 h-4 rounded border-gray-300 text-rose-500 focus:ring-rose-400"
              checked={formData.isPublicEmployer}
              onChange={(e) => onChange('isPublicEmployer', e.target.checked)}
            />
            <label htmlFor="isPublicEmployer" className="text-sm font-medium text-gray-700 cursor-pointer">
              Place entreprise (PE)
            </label>
          </div>

          {/* Validité */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Valide à partir du</label>
              <input
                type="date"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-rose-400 text-sm"
                value={formData.validFrom}
                onChange={(e) => onChange('validFrom', e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Valide jusqu'au</label>
              <input
                type="date"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-rose-400 text-sm"
                value={formData.validUntil}
                onChange={(e) => onChange('validUntil', e.target.value)}
              />
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-rose-400 text-sm resize-none"
              value={formData.notes}
              onChange={(e) => onChange('notes', e.target.value)}
              placeholder="Informations complémentaires…"
            />
          </div>

          {/* Info multi-structures */}
          {selectedCount > 1 && (
            <p className="text-xs text-gray-500 bg-gray-50 px-3 py-2 rounded-md">
              {isEditing
                ? 'Les structures supplémentaires recevront une copie de ce tarif (si pas de doublon).'
                : `Le tarif sera créé pour ${selectedCount} structures avec les mêmes paramètres.`}
            </p>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-md transition">
              Annuler
            </button>
            <button
              type="submit"
              className="flex items-center gap-2 px-4 py-2 text-sm bg-rose-600 text-white rounded-md hover:bg-rose-700 transition disabled:opacity-50"
              disabled={loading || selectedCount === 0}
            >
              <Check size={16} />
              {selectedCount > 1
                ? (isEditing
                    ? `Enregistrer + copier vers ${selectedCount - 1} structure(s)`
                    : `Créer pour ${selectedCount} structures`)
                : 'Enregistrer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Composant principal ─────────────────────────────────────────────────────

// Structure colors for badges
const STRUCT_COLORS = [
  'bg-rose-100 text-rose-700',
  'bg-indigo-100 text-indigo-700',
  'bg-emerald-100 text-emerald-700',
  'bg-amber-100 text-amber-700',
];

const CrecheTariffManagement = () => {
  const [tariffs, setTariffs] = useState([]);
  const [structures, setStructures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterStructureId, setFilterStructureId] = useState('');
  const toast = useToast();

  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [tariffsRes, structuresRes] = await Promise.all([
        getCrecheTariffs(filterStructureId ? { structureId: filterStructureId } : {}),
        getStructures(),
      ]);
      setTariffs(tariffsRes.data);
      setStructures(structuresRes.data);
    } catch (err) {
      setError('Erreur lors du chargement des données');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [filterStructureId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleFieldChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const openCreate = () => {
    setEditingId(null);
    const preSelected = filterStructureId ? [Number(filterStructureId)] : structures.map((s) => s.id);
    setFormData({ ...EMPTY_FORM, structureIds: preSelected });
    setModalOpen(true);
  };

  const openEdit = (tariff, group) => {
    setEditingId(tariff.id);
    // Pre-check all structures that have this tariff in the group
    const allStructureIds = group
      ? group.tariffs.map((t) => t.structureId).filter(Boolean)
      : (tariff.structureId ? [tariff.structureId] : []);
    setFormData({
      structureIds: [...new Set(allStructureIds)],
      name: tariff.name,
      days: tariff.days,
      monthlyAmount: tariff.monthlyAmount,
      isPublicEmployer: tariff.isPublicEmployer ?? false,
      validFrom: tariff.validFrom ? tariff.validFrom.slice(0, 10) : '',
      validUntil: tariff.validUntil ? tariff.validUntil.slice(0, 10) : '',
      notes: tariff.notes || '',
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingId(null);
    setFormData(EMPTY_FORM);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const ids = formData.structureIds || [];
    if (ids.length === 0) return;
    try {
      setLoading(true);
      setError(null);
      if (editingId) {
        const res = await updateCrecheTariff(editingId, {
          structureIds: ids,
          name: formData.name.trim(),
          days: parseInt(formData.days) || 5,
          monthlyAmount: parseFloat(formData.monthlyAmount) || 0,
          isPublicEmployer: formData.isPublicEmployer,
          validFrom: formData.validFrom || null,
          validUntil: formData.validUntil || null,
          notes: formData.notes.trim() || null,
        });
        const created = res.data?.created || [];
        toast.success(created.length > 0
          ? `Tarif mis à jour + ${created.length} copie(s) créée(s)`
          : 'Tarif mis à jour');
      } else {
        await createCrecheTariff({
          structureIds: ids,
          name: formData.name.trim(),
          days: parseInt(formData.days) || 5,
          monthlyAmount: parseFloat(formData.monthlyAmount) || 0,
          isPublicEmployer: formData.isPublicEmployer,
          validFrom: formData.validFrom || null,
          validUntil: formData.validUntil || null,
          notes: formData.notes.trim() || null,
        });
        toast.success(ids.length > 1 ? `Tarif créé pour ${ids.length} structures` : 'Tarif créé');
      }
      closeModal();
      fetchAll();
    } catch (err) {
      setError(err.response?.data?.error || "Erreur lors de l'enregistrement");
      setLoading(false);
    }
  };

  const handleDeleteGroup = async (group) => {
    const count = group.tariffs.length;
    const label = count > 1
      ? `Supprimer "${group.tariffs[0].name}" pour ${count} structures ?`
      : `Supprimer le tarif "${group.tariffs[0].name}" ?`;
    if (!window.confirm(label)) return;
    try {
      setLoading(true);
      setError(null);
      for (const t of group.tariffs) {
        await deleteCrecheTariff(t.id);
      }
      toast.success(count > 1 ? `${count} tarifs supprimés` : 'Tarif supprimé');
      fetchAll();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la suppression');
      setLoading(false);
    }
  };

  // ─── Group tariffs by identity (name + days + PE + validity) ───────────────
  const structureMap = Object.fromEntries(structures.map((s) => [s.id, s.name]));
  const structureColorMap = Object.fromEntries(structures.map((s, i) => [s.id, STRUCT_COLORS[i % STRUCT_COLORS.length]]));

  // Build a key from tariff identity fields
  const tariffKey = (t) => [
    t.name,
    t.days,
    t.isPublicEmployer ? '1' : '0',
    String(t.monthlyAmount),
    (t.validFrom || '').slice(0, 10),
    (t.validUntil || '').slice(0, 10),
  ].join('|');

  // Group tariffs by identity key
  const groupedByIdentity = tariffs.reduce((acc, t) => {
    const key = tariffKey(t);
    if (!acc[key]) acc[key] = { tariffs: [], key };
    acc[key].tariffs.push(t);
    return acc;
  }, {});

  // Convert to array and sort by name, then days
  const tariffGroups = Object.values(groupedByIdentity).sort((a, b) => {
    const ta = a.tariffs[0];
    const tb = b.tariffs[0];
    const nameComp = ta.name.localeCompare(tb.name);
    if (nameComp !== 0) return nameComp;
    return ta.days - tb.days;
  });

  // Filter by structure if needed
  const filteredGroups = filterStructureId
    ? tariffGroups.filter((g) => g.tariffs.some((t) => String(t.structureId) === String(filterStructureId) || !t.structureId))
    : tariffGroups;

  if (loading && tariffs.length === 0 && structures.length === 0) {
    return (
      <div className="px-4 sm:px-6 lg:px-8">
        <Card>
          <CardContent>
            <p className="text-gray-500 py-8 text-center">Chargement...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 space-y-6">
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <CardTitle className="flex items-center gap-2">
              <Receipt size={20} className="text-rose-600" />
              <span>Grilles tarifaires crèche</span>
            </CardTitle>
            {structures.length > 1 && (
              <select
                className="text-sm px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-rose-400"
                value={filterStructureId}
                onChange={(e) => setFilterStructureId(e.target.value)}
              >
                <option value="">Toutes les structures</option>
                {structures.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            )}
          </div>
        </CardHeader>

        <CardContent>
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-4 flex items-start justify-between gap-2">
              <span>{error}</span>
              <button type="button" onClick={() => setError(null)} className="flex-shrink-0 text-red-500 hover:text-red-700">
                <X size={16} />
              </button>
            </div>
          )}

          {filteredGroups.length === 0 ? (
            <p className="text-gray-500 text-sm py-8 text-center">
              Aucun tarif. Cliquez sur + pour en ajouter un.
            </p>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="text-left px-3 py-2.5 font-semibold text-gray-700">Intitulé</th>
                    <th className="text-center px-3 py-2.5 font-semibold text-gray-700">Jours</th>
                    <th className="text-right px-3 py-2.5 font-semibold text-gray-700">Montant</th>
                    <th className="text-center px-3 py-2.5 font-semibold text-gray-700">PE</th>
                    <th className="text-left px-3 py-2.5 font-semibold text-gray-700">
                      <Building size={14} className="inline mr-1" />Structures
                    </th>
                    <th className="text-center px-3 py-2.5 font-semibold text-gray-700">Validité</th>
                    <th className="text-center px-3 py-2.5 font-semibold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredGroups.map((group) => {
                    const rep = group.tariffs[0]; // representative tariff
                    const structIds = group.tariffs.map((t) => t.structureId).filter(Boolean);
                    const hasOrphan = group.tariffs.some((t) => !t.structureId);
                    const validityFrom = fmtDate(rep.validFrom);
                    const validityUntil = fmtDate(rep.validUntil);

                    return (
                      <tr key={group.key} className="border-t hover:bg-rose-50/40 transition">
                        <td className="px-3 py-2.5 font-medium text-gray-800">
                          {rep.name}
                          {rep.notes && (
                            <span className="block text-xs text-gray-400 font-normal truncate max-w-[200px]" title={rep.notes}>
                              {rep.notes}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2.5 text-center text-gray-600">{rep.days}j</td>
                        <td className="px-3 py-2.5 text-right font-semibold text-rose-700">{fmtCurrency(rep.monthlyAmount)}</td>
                        <td className="px-3 py-2.5 text-center"><PEBadge isPE={rep.isPublicEmployer} /></td>
                        <td className="px-3 py-2.5">
                          <div className="flex flex-wrap gap-1">
                            {structIds.map((sid) => (
                              <StructureBadge key={sid} name={structureMap[sid] || '?'} color={structureColorMap[sid] || STRUCT_COLORS[0]} />
                            ))}
                            {hasOrphan && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                                Sans structure
                              </span>
                            )}
                            {structIds.length === structures.length && structures.length > 1 && (
                              <span className="text-xs text-gray-400 ml-1">(toutes)</span>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2.5 text-center text-gray-500 text-xs whitespace-nowrap">
                          {validityFrom || validityUntil ? (
                            <>
                              {validityFrom || '…'}
                              {' → '}
                              {validityUntil || '…'}
                            </>
                          ) : (
                            <span className="text-gray-300">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2.5 text-center">
                          <div className="flex justify-center gap-1">
                            <button
                              onClick={() => openEdit(rep, group)}
                              className="p-1 text-rose-600 hover:bg-rose-50 rounded-full transition"
                              title="Modifier"
                              disabled={loading}
                            >
                              <Edit size={16} />
                            </button>
                            <button
                              onClick={() => handleDeleteGroup(group)}
                              className="p-1 text-red-600 hover:bg-red-50 rounded-full transition"
                              title="Supprimer"
                              disabled={loading}
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* FAB */}
      <button
        onClick={openCreate}
        className="fixed bottom-6 right-6 z-50 bg-rose-600 text-white rounded-full p-4 shadow-lg hover:bg-rose-700 transition-all hover:scale-105"
        title="Ajouter un tarif"
      >
        <Plus size={24} />
      </button>

      <TariffModal
        open={modalOpen}
        title={editingId ? 'Modifier le tarif' : 'Nouveau tarif'}
        formData={formData}
        onChange={handleFieldChange}
        onSubmit={handleSubmit}
        onClose={closeModal}
        loading={loading}
        structures={structures}
        isEditing={!!editingId}
      />
    </div>
  );
};

export default CrecheTariffManagement;
